'use client'

import _ from 'lodash'
import Image from 'next/image'
import {FC} from 'react'

import {BreadcrumbStep} from '@/app/lib/structuredData'
import {BlogOverviewLayout} from '@/components/blog/BlogOverviewLayout'
import {IndustryList} from '@/components/blog/industries/IndustryList'
import {KeyMetrics} from '@/components/blog/metrics/KeyMetrics'
import {BrandGrowthBanner} from '@/components/common/banner/BrandGrowthBanner'
import {Breadcrumbs} from '@/components/common/content/Breadcrumbs'
import {Section} from '@/components/common/content/Section'
import {Hero} from '@/components/common/hero/Hero'
import {Page} from '@/components/Page'
import {getBlogContent} from '@/content/blog/blog'
import {useTranslation} from '@/i18n'
import {
  BLOG_HERO_IMAGE_HEIGHT_DESKTOP,
  BLOG_HERO_IMAGE_HEIGHT_MOBILE,
  BLOG_HERO_IMAGE_WIDTH_DESKTOP,
  BLOG_HERO_IMAGE_WIDTH_MOBILE,
  HERO_PADDING_DESKTOP,
  HERO_PADDING_MOBILE,
} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {BlogOverview, Industry, IndustryName} from '@/utils/contentful'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

type BlogPageProps = {
  blogs: BlogOverview[]
  currentPage: number
  numberOfPages: number
  totalItems: number
  breadcrumbTrail: BreadcrumbStep[]
  industryName: IndustryName
  industries: Industry[]
}

const BlogPage: FC<React.PropsWithChildren<BlogPageProps>> = ({
  blogs,
  currentPage,
  numberOfPages,
  totalItems,
  breadcrumbTrail,
  industryName,
  industries,
}) => {
  const {t} = useTranslation()
  const {
    header,
    industries: {heroTitle, heroSubtitle},
    getStarted: {title, buttonLinkHref, buttonLinkText},
  } = getBlogContent()
  const [[industry], otherIndustries] = _.partition(industries, ({name}) => name === industryName)

  return (
    <>
      <Page
        mainCtaButton={{
          text: t(header.buttonLinkText),
          linkHref: header.buttonLinkHref,
          onClick: () =>
            analytics.track('Click on Start registration (Blog - Header)', {
              industry: industryName,
            }),
        }}
      >
        <Hero
          breadcrumbs={<Breadcrumbs trail={breadcrumbTrail} />}
          title={t(`${heroTitle}-${industry.name}`)}
          subtitle={t(`${heroSubtitle}-${industry.name}`)}
        >
          <div className="image">
            <Image
              src={industry.heroImage.url}
              alt={industry.heroImage.alt}
              sizes={`${MOBILE_SCREENSIZE} 190px, 340px`}
              fill
              priority
              style={{objectFit: 'contain'}}
            />
          </div>
        </Hero>
        <Section hasPaddingTop>
          <KeyMetrics
            importance={industry.importanceMetric}
            willingness={industry.willingnessMetric}
          />
        </Section>
        <BlogOverviewLayout
          blogs={blogs}
          currentPage={currentPage}
          numberOfPages={numberOfPages}
          totalItems={totalItems}
        />
        <Section hasPaddingTop hasPaddingBottom backgroundColor={theme.palette.background.butter}>
          <IndustryList industries={otherIndustries} />
        </Section>
        <Section hasPaddingTop hasPaddingBottom>
          <BrandGrowthBanner
            title={t(title)}
            buttonLinkHref={buttonLinkHref}
            buttonLinkText={t(buttonLinkText)}
            onClick={() => analytics.track('BrandGrowthBanner-Blogs')}
          />
        </Section>
      </Page>

      <style jsx>{`
        .image {
          position: relative;
          width: ${BLOG_HERO_IMAGE_WIDTH_DESKTOP};
          height: ${BLOG_HERO_IMAGE_HEIGHT_DESKTOP};
          margin-left: ${theme.spacing[7]};
          margin-bottom: calc(-${BLOG_HERO_IMAGE_HEIGHT_DESKTOP} / 2 - ${HERO_PADDING_DESKTOP});
        }

        @media ${TABLET_SCREENSIZE} {
          .image {
            margin-left: 0;
            margin-top: ${theme.spacing[6]};
          }
        }

        @media ${MOBILE_SCREENSIZE} {
          .image {
            width: ${BLOG_HERO_IMAGE_WIDTH_MOBILE};
            height: ${BLOG_HERO_IMAGE_HEIGHT_MOBILE};
            margin-top: ${theme.spacing[5]};
            margin-bottom: calc(-${BLOG_HERO_IMAGE_HEIGHT_MOBILE} / 2 - ${HERO_PADDING_MOBILE});
          }
        }
      `}</style>
    </>
  )
}

export default BlogPage
